import {generateXReqId} from "../../utils";
import {useCallback, useEffect, useRef, useState} from "react";
import {RotatingLines} from "react-loader-spinner";
import {toast} from "react-toastify";
import {ConfirmationDialog} from "../../components/confirmationDialog";
import {formStyle, validLengthAndOnlyDigits} from "./index";

export const InsertForm = () => {
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [confirmDialogText, setConfirmDialogText] = useState('');
	const [loading, setLoading] = useState(false);
	const [isDisabledBasicFields, setIsDisabledBasicFields] = useState(false);
	const [isDisabledIdFields, setIsDisabledIdFields] = useState(false);

	const [afmValue, setAfmValue] = useState('');
	const [amkaValue, setAmkaValue] = useState('');
	const [documentIdValue, setDocumentIdValue] = useState('');

	const [afmError, setAfmError] = useState("");
	const [amkaError, setAmkaError] = useState("");
	const [formValid, setFormValid] = useState(false);

	// init expiration date to next month
	let datePlus30 = new Date();
	datePlus30.setMonth(datePlus30.getMonth() + 1);
	const [expiryDate, setExpiryDate] = useState(datePlus30.toISOString().split("T")[0]);
	const [duration, setDuration] = useState("");

	const [insertInputValues, setInsertInputValues] = useState({
		afm: "",
		amka: "",
		documentId: "",
		firstName: "",
		lastName: "",
		fatherName: "",
		birthdate: ""
	});

	const afmInputRef = useRef(null);
	const amkaInputRef = useRef(null);

	const updateSearchInputValues = (event) => {
		const {name, value} = event.target;
		const trimmedValue = (name === "afm" || name === "amka" || name === "documentId") ? value.replace(/\s/g, '') : value;
		setInsertInputValues((prevState) => ({
			...prevState,
			[name]: trimmedValue
		}));
	};

	const validateForm = useCallback((withMsg) => {
		const {afm, amka, documentId, firstName, lastName, fatherName, birthdate} = insertInputValues;

		/*
		 * handle ID (basic) fields
		 */
		setAfmValue(afm);
		setAmkaValue(amka);
		setDocumentIdValue(documentId);

		const validAfm = insertInputValues.afm.length === 0 || validLengthAndOnlyDigits(insertInputValues.afm, 9);
		if (!validAfm) {
			if (withMsg) setAfmError("Το ΑΦΜ πρέπει να αποτελείται από 9 ψηφία");
		} else
			setAfmError("");

		const validAmka = (insertInputValues.amka.length === 0 || validLengthAndOnlyDigits(insertInputValues.amka, 11));
		if (!validAmka) {
			if (withMsg) setAmkaError("Το AMKA πρέπει να αποτελείται από 11 ψηφία");
		} else
			setAmkaError("");

		(afm.trim() || amka.trim() || documentId.trim()) ? setIsDisabledIdFields(true) : setIsDisabledIdFields(false);

		/*
		 * disable ID (basic) fields, if name search fields are entered
		 */
		if (firstName.trim() || lastName.trim() || fatherName.trim() || birthdate.trim()) {
			setIsDisabledBasicFields(true);
		} else {
			setIsDisabledBasicFields(false);
		}

		/*
		 * The form is valid if
		 * - at least one ID (afm, amka, docId) is populated correctly OR
		 * - all name fields + birthdate are populated
		 */
		const valid = (validAfm && validAmka &&
						!(insertInputValues.afm.length === 0 && insertInputValues.amka.length === 0 && insertInputValues.documentId.length === 0)) ||
				(firstName.trim() && lastName.trim() && fatherName.trim() && birthdate.trim());

		setFormValid(valid);
		return valid;
	}, [insertInputValues]);

	useEffect(() => {
		validateForm(false);
	}, [validateForm]);

	const handleDurationChange = (e) => {
		const value = e.target.value;
		setDuration(value);
		if (value) {
			const today = new Date();
			const newExpiryDate = new Date(today.setMonth(today.getMonth() + parseInt(value, 10)));
			setExpiryDate(newExpiryDate.toISOString().split("T")[0]);
		}
	};

	const handleInsertSubmit = async (event) => {
		event.preventDefault();

		if (!validateForm("submit") || expiryDate.length === 0) {
			toast.error("Συμπληρώστε ΑΦΜ ή ΑΜΚΑ ή Αριθμό εγγράφου (ή συνδυασμό) και ημερομηνία λήξης");
			return;
		}

		setLoading(true);

		// TODO ::: 2025-04-08 IG : Call /info endpoint and show the retrieved names in the confirmation dialog
		// send insertion request
		const suspensionInfoRequest = {
			afm: insertInputValues.afm || null,
			amka: insertInputValues.amka || null,
			documentId: insertInputValues.documentId || null,
			firstName: insertInputValues.firstName || null,
			fathersName: insertInputValues.fatherName || null,
			lastName: insertInputValues.lastName || null,
			dateOfBirth: insertInputValues.birthdate || null,
			endDate: new Date(`${expiryDate}T23:59:59.999Z`).toISOString() || null
		};

		const res = await fetch("/api/blackList-administration/info", {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"x-request-id": generateXReqId()
			},
			body: JSON.stringify(suspensionInfoRequest)
		});
		try {
			if (res.ok) {
				const data = await res.json();
				if (data.length > 0) {
					const message = [
						"Πρόκειται να υποβάλετε εγγραφή αναστολής πρόσβασης:",
						...data.map(item => item.names + " (" + item.ticketsCount + " ενεργά εισιτήρια)"),
						"\nΘέλετε να προχωρήσετε;"
					].join("\n");
					setConfirmDialogText(message);
				} else {
					setConfirmDialogText("Είστε σίγουροι ότι θέλετε να υποβάλετε την εγγραφή;");
				}
				setShowConfirmDialog(true);
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.error;
				toast.error(appError);
			} catch (err) {
				toast.error(errorResponse);
			}
			resetInsertForm();
		}
		setLoading(false);
	};

	const handleConfirm = async () => {
		setShowConfirmDialog(false);
		setLoading(true);

		// send insertion request
		const addRequest = {
			afm: insertInputValues.afm || null,
			amka: insertInputValues.amka || null,
			documentId: insertInputValues.documentId || null,
			firstName: insertInputValues.firstName || null,
			fathersName: insertInputValues.fatherName || null,
			lastName: insertInputValues.lastName || null,
			dateOfBirth: insertInputValues.birthdate || null,
			endDate: new Date(`${expiryDate}T23:59:59.999Z`).toISOString() || null
		};

		const res = await fetch("/api/blackList-administration/add", {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"x-request-id": generateXReqId()
			},
			body: JSON.stringify(addRequest)
		});
		try {
			if (res.ok) {
				toast.success("Η προσθήκη έγινε επιτυχώς");
			} else {
				// noinspection ExceptionCaughtLocallyJS
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.error;
				toast.error(appError);
			} catch (err) {
				toast.error(errorResponse);
			}
		}

		resetInsertForm();

		setLoading(false);
	};

	const handleCancel = () => {
		setShowConfirmDialog(false);
	};

	const updateInsertInputValues = (event) => {
		const {name, value} = event.target;

		const trimmedValue = (name === "afm" || name === "amka" || name === "documentId") ? value.replace(/\s/g, '') : value;
		setInsertInputValues((prevState) => ({
			...prevState,
			[name]: trimmedValue
		}));
	};

	const validateField = () => {
		validateForm(true);
	};

	const handleExpiryDateChange = (e) => {
		const date = e.target.value;
		setExpiryDate(date);
		setDuration('');
	};

	const resetInsertForm = () => {
		setInsertInputValues({
			afm: "",
			amka: "",
			documentId: "",
			firstName: "",
			lastName: "",
			fatherName: "",
			birthdate: ""
		});
		setExpiryDate(datePlus30.toISOString().split("T")[0]);
		setDuration('');
	};

	return (
			<div>
				{loading ? (
						<div className="d-flex flex-fill justify-content-center align-items-center">
							<RotatingLines
									visible={true}
									height="96"
									width="96"
									color="grey"
									strokeWidth="5"
									strokeColor="#003476"
									animationDuration="0.75"
									ariaLabel="rotating-lines-loading"
							/>
						</div>
				) : (
						<div>
							<form style={{...formStyle}} onSubmit={handleInsertSubmit}>
								<h3 style={{textAlign: "center", marginBottom: "20px", color: "#007BFF"}}>Προσθήκη εγγραφής με:</h3>
								<div style={{
									marginBottom: "20px",
									padding: "15px",
									border: "1px solid #ddd",
									borderRadius: "8px",
									backgroundColor: "#f9f9f9"
								}}>
									<h4 style={{textAlign: "center", marginBottom: "10px", color: "#555"}}>ΑΦΜ ή ΑΜΚΑ ή Αριθμό εγγράφου
										(ή συνδυασμό) και ημερομηνία λήξης</h4>
									<div style={{marginBottom: "15px"}}>
										<label htmlFor="afm" className="form-label">ΑΦΜ</label>
										<input ref={afmInputRef} name="afm" id="afm" value={afmValue} type="text"
											   className="form-control form-control-lg"
											   onChange={updateInsertInputValues}
											   onBlur={validateField}
											   disabled={isDisabledBasicFields}
										/>
										{afmError && <span style={{color: "red", fontSize: "13px"}}>{afmError}</span>}
									</div>
									<div style={{marginBottom: "15px"}}>
										<label htmlFor="amka" className="form-label">ΑΜΚΑ</label>
										<input ref={amkaInputRef} name="amka" id="amka" value={amkaValue} type="text"
											   className="form-control form-control-lg"
											   onChange={updateInsertInputValues}
											   onBlur={validateField}
											   disabled={isDisabledBasicFields}
										/>
										{amkaError && <span style={{color: "red", fontSize: "13px"}}>{amkaError}</span>}
									</div>
									<div style={{marginBottom: "15px"}}>
										<label htmlFor="documentId" className="form-label">Αριθμός εγγράφου</label>
										<input name="documentId" id="documentId" value={documentIdValue} type="text"
											   className="form-control form-control-lg"
											   onChange={updateInsertInputValues}
											   disabled={isDisabledBasicFields}
										/>
									</div>

									<h4 style={{textAlign: "center", marginBottom: "10px", color: "#555"}}>ή</h4>
									{/*2nd Section*/}
									<div style={{
										marginBottom: "20px",
										padding: "15px",
										border: "1px solid #ddd",
										borderRadius: "8px",
										backgroundColor: "#f9f9f9"
									}}>
										<h4 style={{textAlign: "center", marginBottom: "10px", color: "#555"}}>Στοιχεία
											ταυτότητας για αναζήτηση μέσω ΑΦΜ</h4>
										<div style={{marginBottom: "15px"}}>
											<label htmlFor="firstName" className="form-label">Όνομα</label>
											<input name="firstName" id="firstName" type="text"
												   className="form-control form-control-lg"
												   disabled={isDisabledIdFields}
												   onChange={updateSearchInputValues}/>
										</div>
										<div style={{marginBottom: "15px"}}>
											<label htmlFor="lastName" className="form-label">Επώνυμο</label>
											<input name="lastName" id="lastName" type="text"
												   className="form-control form-control-lg"
												   disabled={isDisabledIdFields}
												   onChange={updateSearchInputValues}/>
										</div>
										<div style={{marginBottom: "15px"}}>
											<label htmlFor="fatherName" className="form-label">Πατρώνυμο</label>
											<input name="fatherName" id="fatherName" type="text"
												   className="form-control form-control-lg"
												   disabled={isDisabledIdFields}
												   onChange={updateSearchInputValues}/>
										</div>
										<div style={{marginBottom: "15px"}}>
											<label htmlFor="birthdate" className="form-label">Ημερομηνία γέννησης</label>
											<input name="birthdate" id="birthdate" type="date" max={new Date().toISOString().split("T")[0]}
												   className="form-control form-control-lg"
												   disabled={isDisabledIdFields}
												   onChange={updateSearchInputValues}/>
										</div>
									</div>

									<div style={{display: "flex", gap: "15px", marginBottom: "15px"}}>
										<div style={{width: "50%"}}>
											<label htmlFor="banEndDate" className="form-label">Ημερομηνία λήξης</label>
											<div style={{display: "flex", gap: "5px"}}>
												<input name="banEndDate" id="banEndDate" type="date" value={expiryDate}
													   onChange={handleExpiryDateChange}
													   min={new Date().toISOString().split("T")[0]}
													   className="form-control form-control-lg"
													   style={{textAlign: "center"}}/>
											</div>
										</div>
										<div style={{width: "50%"}}>
											<label htmlFor="banDuration" className="form-label">Διάρκεια
												(μήνες)</label>
											<input type="number" name="banDuration" id="banDuration" value={duration}
												   onChange={handleDurationChange} min="1"
												   className="form-control form-control-lg"
												   style={{width: "50%", textAlign: "center"}}/>
										</div>
									</div>
								</div>
								{/*<div className="d-flex justify-content-end">*/}
								<div style={{display: "flex", gap: "10px", marginTop: "15px", justifyContent: "center"}}>
									<button type="reset" onClick={resetInsertForm}
											className="btn btn-outline-primary btn-lg">Καθαρισμός
									</button>
									<button type="submit" className="btn btn-primary btn-lg" disabled={!formValid}>Προσθήκη</button>
								</div>
							</form>
							<ConfirmationDialog
									isVisible={showConfirmDialog}
									onConfirm={handleConfirm}
									onCancel={handleCancel}
									message={confirmDialogText}
									confirmText="Προσθήκη"
									cancelText="Άκυρο"/>
						</div>)}
			</div>
	);
};
